import { isP2PLikeSessionType } from '@/utils/sessionType';

const sessionHasPatientParticipant = (
	session: Pick<Sessions.Session, 'participants'>,
): boolean =>
	session.participants?.some(
		(participant) =>
			participant.entity_type === 'PATIENT' ||
			participant.patient_id != null,
	) ?? false;

/** Sessão P2P (tipo ou participante paciente). */
export const isP2PPatientSession = (
	session: Pick<Sessions.Session, 'type' | 'participants'>,
): boolean =>
	isP2PLikeSessionType(session.type) || sessionHasPatientParticipant(session);

/** P2P agendada pelo próprio profissional para paciente dele. */
export const isSessionScheduledByProfessional = (
	session: Pick<Sessions.Session, 'scheduled_by' | 'type'>,
): boolean =>
	(session.type === 'P2P' || session.type === 'P2P_TRANSCRIPTION_ONLY') &&
	session.scheduled_by === 'PROFESSIONAL';

/** P2P com paciente: profissional pode compartilhar link (sem filtro por scheduled_by). */
export const canProfessionalShareP2PPatientAccessLink = (
	session: Pick<Sessions.Session, 'type' | 'participants'>,
): boolean => isP2PPatientSession(session);

/** Sessão já iniciada, encerrada ou cancelada. */
export const isSessionStartedOrCompleted = (
	session: Pick<Sessions.Session, 'status' | 'started_at'>,
): boolean =>
	session.status === 'STARTED' ||
	session.status === 'FINISHED' ||
	session.status === 'CANCELLED' ||
	session.started_at != null;

/**
 * Profissional pode cancelar qualquer sessão própria (DEFAULT, P2P, GROUP...)
 * que ainda não começou. O backend aplica a mesma regra e devolve o crédito ao paciente.
 */
export const canProfessionalCancelSession = (
	session: Pick<Sessions.Session, 'status' | 'started_at'>,
): boolean => !isSessionStartedOrCompleted(session);

/** P2P do profissional que ainda pode ser editada ou cancelada. */
export const canProfessionalManageP2PSession = (
	session: Sessions.Session,
): boolean =>
	isSessionScheduledByProfessional(session) &&
	!isSessionStartedOrCompleted(session);

export type ProfessionalSessionBookingSource =
	| 'self'
	| 'plumaa_user'
	| 'plumaa_team';

const sessionHasUserParticipant = (session: Sessions.Session): boolean =>
	!!session.user_id ||
	(session.participants?.some(
		(participant) =>
			participant.entity_type === 'USER' || participant.user_id != null,
	) ??
		false);

/** Origem do agendamento para exibição no painel do profissional. */
export const getProfessionalSessionBookingSource = (
	session: Sessions.Session,
): ProfessionalSessionBookingSource => {
	if (isSessionScheduledByProfessional(session)) return 'self';

	if (session.scheduled_by === 'USER') return 'plumaa_user';

	if (
		session.scheduled_by === 'ADMIN' ||
		session.scheduled_by === 'SUPPORT'
	) {
		return 'plumaa_team';
	}

	if (
		isP2PLikeSessionType(session.type) &&
		sessionHasPatientParticipant(session)
	) {
		return 'self';
	}

	if (sessionHasUserParticipant(session)) return 'plumaa_user';

	return 'plumaa_team';
};

export const PROFESSIONAL_SESSION_BOOKING_SOURCE_COPY: Record<
	ProfessionalSessionBookingSource,
	{ label: string; description: string }
> = {
	self: {
		label: 'Agendada por você',
		description: 'Você agendou esta consulta para o seu paciente.',
	},
	plumaa_user: {
		label: 'Agendada por usuário Plumaa',
		description: 'Um usuário da plataforma reservou este horário com você.',
	},
	plumaa_team: {
		label: 'Agendada pela equipe Plumaa',
		description:
			'Esta consulta foi criada pelo suporte ou administração Plumaa.',
	},
};
